import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/admin-api';
import toast from 'react-hot-toast';
import DetailSection from '../shared/DetailSection';
import Badge from '../shared/Badge';
import EmptyState from '../shared/EmptyState';
import { getActionLabel, getActionBadgeVariant, getResourceTypeLabel, formatTimestamp } from '../../utils/activityHelpers';

// Activity icon SVG path
const ACTIVITY_ICON = 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01';

export default function ProductActivityTab({ productId }) {
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedActivity, setExpandedActivity] = useState(null);

  useEffect(() => {
    if (productId) {
      loadActivities();
    }
  }, [productId]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const data = await adminApi.products.getActivities(productId, 100, 0);
      setActivities(data.activities || []);
      setTotalCount(data.total_count || 0);
    } catch (error) {
      console.error('Failed to load activity data:', error);
      toast.error('Failed to load activity history');
    } finally {
      setLoading(false);
    }
  };

  const toggleActivityDetails = (activityId) => {
    setExpandedActivity(expandedActivity === activityId ? null : activityId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-gray-400">
          <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <span>Loading activity history...</span>
        </div>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="p-8 max-w-5xl">
        <EmptyState
          icon={ACTIVITY_ICON}
          title="No Activity Yet"
          subtitle="No activity has been recorded for this product"
        />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl">
      <div className="space-y-6">
        <DetailSection
          title="Activity History"
          subtitle={`${totalCount} total ${totalCount === 1 ? 'activity' : 'activities'}`}
        >
          <div className="space-y-3">
            {activities.map((activity) => (
              <div
                key={activity.id}
                className="bg-gray-800/30 border border-gray-700 rounded-lg overflow-hidden hover:border-gray-600 transition-colors"
              >
                {/* Main Row */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer"
                  onClick={() => toggleActivityDetails(activity.id)}
                >
                  <div className="flex items-center gap-4 flex-1">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center" style={{ color: 'var(--color-text-on-accent)' }}>
                                              <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ACTIVITY_ICON} />
                        </svg>
                      </div>
                    </div>

                    {/* Activity Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          variant={getActionBadgeVariant(activity.action)}
                          className="text-xs font-medium"
                        >
                          {getActionLabel(activity.action)}
                        </Badge>
                        {activity.resource_type && (
                          <span className="text-sm text-gray-400">
                            {getResourceTypeLabel(activity.resource_type)}
                          </span>
                        )}
                        {activity.username && (
                          <span className="text-sm text-gray-500">
                            by <span className="text-gray-400">{activity.username}</span>
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatTimestamp(activity.occurred_at)}
                        {activity.ip_address && (
                          <span className="ml-2">• {activity.ip_address}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expand Arrow */}
                  {activity.activity_metadata && Object.keys(activity.activity_metadata).length > 0 && (
                    <div className="flex-shrink-0 ml-4">
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${
                          expandedActivity === activity.id ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Expanded Details */}
                {expandedActivity === activity.id && activity.activity_metadata && (
                  <div className="border-t border-gray-700 bg-gray-900/50 p-4">
                    <div className="text-sm text-gray-300">
                      <div className="font-semibold text-gray-200 mb-2">Details:</div>
                      <div className="space-y-1">
                        {Object.entries(activity.activity_metadata).map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <span className="text-gray-500 min-w-[140px]">{key.replace(/_/g, ' ')}:</span>
                            <span className="text-gray-300 break-all">
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DetailSection>
      </div>
    </div>
  );
}
